import { useCallback, useRef, useState } from "react"
import {
  alpha,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { UploadFile as UploadIcon } from "@mui/icons-material"

export interface DropZoneProps {
  variant: "mini" | "full"
  onFiles: (files: File[]) => void
  disabled?: boolean
  /** mini only: вызывается при клике по зоне (не по ссылке «выберите файлы») */
  onClick?: () => void
}

function filterJson(fileList: FileList | null): File[] {
  return Array.from(fileList ?? []).filter((f) => f.name.endsWith(".json"))
}

export function DropZone({
  variant,
  onFiles,
  disabled = false,
  onClick,
}: DropZoneProps) {
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      if (disabled) return
      const files = filterJson(e.dataTransfer.files)
      if (files.length) onFiles(files)
    },
    [disabled, onFiles],
  )

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled) setDragOver(true)
  }

  const handleDragLeave = () => setDragOver(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = filterJson(e.target.files)
    if (files.length) onFiles(files)
    e.target.value = ""
  }

  const activeBorder = dragOver
    ? theme.palette.info.main
    : theme.palette.divider

  const activeBg = dragOver
    ? alpha(theme.palette.info.main, 0.06)
    : "transparent"

  if (variant === "mini") {
    return (
      <Paper
        variant="outlined"
        sx={{
          borderStyle: "dashed",
          borderWidth: 1,
          borderColor: activeBorder,
          backgroundColor: activeBg,
          transition: "all 0.15s ease",
          borderRadius: 1,
          cursor: disabled ? "default" : "pointer",
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && onClick?.()}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ px: 1.5, py: 4 }}
        >
          <UploadIcon
            fontSize="small"
            sx={{ color: "text.disabled", flexShrink: 0 }}
          />
          <Typography variant="caption" color="text.secondary" noWrap>
            Перетащите JSON <br />
            или{" "}
            <Box
              component="span"
              sx={{ color: "info.main", textDecoration: "underline" }}
              onClick={(e) => {
                e.stopPropagation()
                if (!disabled) inputRef.current?.click()
              }}
            >
              выберите файлы
            </Box>
          </Typography>
        </Stack>
        <input
          ref={inputRef}
          type="file"
          accept=".json"
          multiple
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
      </Paper>
    )
  }

  // variant === "full"
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderStyle: "dashed",
        borderWidth: 2,
        borderColor: activeBorder,
        backgroundColor: activeBg,
        transition: "all 0.2s ease",
        cursor: "default",
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <Stack spacing={2} alignItems="center">
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <UploadIcon sx={{ fontSize: 28, color: "info.main" }} />
        </Box>

        <Box textAlign="center">
          <Typography variant="subtitle1" gutterBottom>
            Перетащите файлы сюда
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Один или несколько <code>result.json</code> из экспорта Telegram
            Desktop
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<UploadIcon />}
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Выбрать файлы
        </Button>

        <input
          ref={inputRef}
          type="file"
          accept=".json"
          multiple
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
      </Stack>
    </Paper>
  )
}
